import React, { useState } from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import { useAppStore } from "../../../../../providers/AppStore";
import { useAuth } from "../../../../../providers/AuthProvider";
import LoadingSpinner from "../../../../../common/ui/LoadingSpinner";
import { RootStatePlatform } from "../../../store/platform-slice";
import { useSubject } from "../../../providers/SubjectProvider";
import {
  isResourcePending,
  usePlatformResource,
} from "../../../hooks/usePlatformResource";
import ScrollableWrapper from "../../common/ScrollableWrapper";
import PlatformTable from "../../programming/PlatformTable";
import PillTabs from "../../../../consultation/components/section2-systems/PillTabs";
import EnergyTrainingChart from "../../../../consultation/components/section2-systems/EnergyTrainingChart";
import { EnergyTrainingZone } from "../../../../consultation/data/consultationTypes";
import {
  ENERGY_TRAINING_SYSTEM_NAME,
  buildTrainingTableInsights,
  buildTrainingTableRows,
  hasTrainingTableData,
} from "./energyTrainingData";
import { hasZoneData } from "../../../../consultation/data/energyTrainingZones";

type View = "Graph" | "Table";

/** Graph is the default view (product decision, SD-6793). */
const VIEWS: View[] = ["Graph", "Table"];

/** The consultation panel's empty-state copy. */
const EMPTY_COPY = "No training-zone data is available for this client yet.";

/**
 * Energy & Training Zones — the Health overview's detail column for the
 * synthetic system of the same name (no score, like the consultation's
 * Energy & Training panel). Two views:
 *  - Graph: the consultation's zones chart, from `GetConsultation`
 *    `energyTraining.zones` (the shared "consultation" resource).
 *  - Table: the Training Program table that used to sit in Programming ›
 *    Workout, from the report's `programming.workoutPlan` (already loaded by
 *    the screen), with the backend's headers and per-zone insight tooltips.
 */
const EnergyTrainingSystem = () => {
  const theme: Theme = useTheme();
  const [view, setView] = useState<View>("Graph");

  const { subjectId } = useSubject();
  const { AuthUser } = useAuth();
  const { ASConsultation } = useAppStore();
  const consultationStatus = usePlatformResource(
    "consultation",
    subjectId,
    AuthUser?.role
  );
  const zones: EnergyTrainingZone[] =
    ASConsultation(subjectId)?.energyTraining?.zones ?? [];

  const workoutPlan = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer?.programming?.workoutPlan
  );

  const renderGraph = () => {
    if (isResourcePending(consultationStatus)) {
      return (
        <div style={styles.centered}>
          <LoadingSpinner
            size="small"
            color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
          />
        </div>
      );
    }
    if (!hasZoneData(zones)) {
      return <div style={styles.empty(theme)}>{EMPTY_COPY}</div>;
    }
    return <EnergyTrainingChart zones={zones} />;
  };

  const renderTable = () => {
    if (!workoutPlan || !hasTrainingTableData(workoutPlan)) {
      return <div style={styles.empty(theme)}>{EMPTY_COPY}</div>;
    }
    return (
      <PlatformTable
        data={buildTrainingTableRows(workoutPlan)}
        insightData={buildTrainingTableInsights(workoutPlan)}
      />
    );
  };

  return (
    <div style={styles.container}>
      <ScrollableWrapper customPadding={"0"}>
        <div style={styles.inner(theme)}>
          <div style={styles.headerRow}>
            <div style={styles.title(theme)}>{ENERGY_TRAINING_SYSTEM_NAME}</div>
            <PillTabs
              options={VIEWS}
              active={view}
              onSelect={(v) => setView(v as View)}
            />
          </div>
          {view === "Graph" ? renderGraph() : renderTable()}
        </div>
      </ScrollableWrapper>
    </div>
  );
};

export default EnergyTrainingSystem;

// Container, card and title mirror SystemDetails so the column reads as one
// more system.
const styles = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
    minWidth: 0,
  },
  inner: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "12px",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
  }),
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: "1.4",
    letterSpacing: "-0.36px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  empty: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: "24px 0",
  },
};
